import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ChartLine, 
  GraduationCap, 
  ShoppingBag, 
  Lightning, 
  ArrowRight,
  CheckCircle,
  Star
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const Home = () => {
  const features = [
    {
      icon: ChartLine,
      title: 'Live Trading Signals',
      description: 'Real-time buy/sell signals from professional traders with detailed analysis.'
    },
    {
      icon: GraduationCap,
      title: 'Expert Courses',
      description: 'Comprehensive video courses from beginner to advanced trading strategies.'
    },
    {
      icon: ShoppingBag,
      title: 'Exclusive Merchandise',
      description: 'Premium trading gear and accessories for the modern trader.'
    },
    {
      icon: Lightning,
      title: 'Live Charts',
      description: 'Professional-grade charts powered by TradingView integration.'
    }
  ];

  const tiers = [
    {
      name: 'Basic',
      price: 'Free',
      features: ['View limited signals', 'Basic charts', 'Community access'],
      accent: 'silver'
    },
    {
      name: 'Premium',
      price: '$25',
      features: ['All trading signals', 'Premium charts', '5 courses', 'Shop discounts', 'Email support'],
      accent: 'white',
      popular: true
    },
    {
      name: 'Elite',
      price: '$50',
      features: ['Everything in Premium', 'All courses', 'Live trading room', 'Priority support', 'Exclusive merch'],
      accent: 'gold'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1647101734210-ff5d79813069?w=1920)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/80 to-[#050505]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white mb-6" data-testid="hero-title">
              Master the Markets with
              <span className="block text-[#D4AF37] font-medium">THE VAULT by 1000Banks</span>
            </h1>
            <p className="text-lg sm:text-xl text-zinc-400 mb-8 max-w-2xl mx-auto leading-relaxed" data-testid="hero-subtitle">
              Join thousands of traders who have transformed their trading journey with our expert courses, live signals, and exclusive community.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button 
                  className="bg-[#D4AF37] text-black font-medium hover:bg-[#E5C158] px-8 py-6 text-lg gold-glow"
                  data-testid="hero-cta-primary"
                >
                  Start Trading Today
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/courses">
                <Button 
                  variant="outline"
                  className="border-zinc-700 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] px-8 py-6 text-lg"
                  data-testid="hero-cta-secondary"
                >
                  Explore Courses
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit text-3xl sm:text-4xl font-light text-white mb-4">
              Everything You Need to <span className="text-[#D4AF37]">Succeed</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Our comprehensive platform provides all the tools and education you need to become a profitable trader.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#121212] border border-white/5 p-6 card-hover"
                data-testid={`feature-card-${index}`}
              >
                <feature.icon className="w-10 h-10 text-[#D4AF37] mb-4" weight="duotone" />
                <h3 className="font-outfit text-lg font-medium text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trading Preview Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-outfit text-3xl sm:text-4xl font-light text-white mb-6">
                Professional-Grade <span className="text-[#D4AF37]">Trading Tools</span>
              </h2>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Access the same tools used by professional traders. Our TradingView integration provides real-time charts, technical analysis, and market data.
              </p>
              <ul className="space-y-4 mb-8">
                {['Real-time market data', 'Advanced charting tools', 'Custom indicators', 'Price alerts'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37]" weight="fill" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/trading">
                <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]" data-testid="trading-cta">
                  View Live Charts
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1735469157670-1212e570eadc?w=800"
                alt="Trading Dashboard"
                className="w-full border border-white/10"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit text-3xl sm:text-4xl font-light text-white mb-4">
              Choose Your <span className="text-[#D4AF37]">Trading Level</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Start for free and upgrade as you grow. All plans include access to our trading community.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {tiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-[#121212] border p-8 card-hover ${
                  tier.accent === 'gold' 
                    ? 'border-[#D4AF37]/30 gold-glow' 
                    : tier.accent === 'white'
                    ? 'border-white/20'
                    : 'border-white/5'
                }`}
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-xs font-bold px-4 py-1">
                    MOST POPULAR
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className={`font-outfit text-xl font-medium mb-2 ${
                    tier.accent === 'gold' ? 'text-[#D4AF37]' : 
                    tier.accent === 'silver' ? 'text-[#C0C0C0]' : 'text-white'
                  }`}>
                    {tier.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-outfit font-semibold text-white">{tier.price}</span>
                    {tier.price !== 'Free' && <span className="text-zinc-500">/month</span>}
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-zinc-400">
                      <Star className={`w-4 h-4 ${
                        tier.accent === 'gold' ? 'text-[#D4AF37]' : 'text-zinc-500'
                      }`} weight="fill" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/pricing" className="block">
                  <Button 
                    className={`w-full ${
                      tier.accent === 'gold' 
                        ? 'bg-[#D4AF37] text-black hover:bg-[#E5C158]' 
                        : 'bg-transparent border border-zinc-700 text-white hover:border-[#D4AF37]'
                    }`}
                  >
                    {tier.price === 'Free' ? 'Get Started' : 'Subscribe Now'}
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-outfit text-3xl sm:text-4xl font-light text-white mb-6">
              Ready to Start Your <span className="text-[#D4AF37]">Trading Journey?</span>
            </h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Join thousands of successful traders with THE VAULT by 1000Banks.
            </p>
            <Link to="/register">
              <Button 
                className="bg-gradient-to-r from-[#D4AF37] to-[#B38F24] text-black shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] px-10 py-6 text-lg"
                data-testid="final-cta"
              >
                Create Free Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#050505] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <ChartLine className="w-6 h-6 text-[#D4AF37]" weight="duotone" />
              <span className="font-outfit text-lg font-medium text-white">
                THE VAULT<span className="text-[#D4AF37]"> by 1000Banks</span>
              </span>
            </div>
            <p className="text-sm text-zinc-500">
              © 2026 THE VAULT by 1000Banks. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
